<script>
import BtnComoponent from './Btn.vue'
import DateFieldComponent from './DateField.vue'
import DatepickerComponent from './Datepicker.vue'
import DisabledTipComponent from './DisabledTip.vue'
import store from '../store'
import { getDefaultDeparture } from '../stayRules'

export default {
    // state
    data () {
        return {
            arrivalOnOpen: null,
            departureOnOpen: null,
            fieldsOnOpen: null,
            isDatepickerExpanded: null,
            selecting: null,
        }
    },
    props: {
        revertExclusionSelectors: {
            default: [],
            type: Array,
        },
        hasClearDatesBtn: Boolean,
        stayLength: Number,
        stayLengthMax: Number,
        // availability
        availability: Object,
        hasAvailabilityData: Boolean,
        isGettingAvailability: Boolean,
        stayRules: Object,
        // date
        arrival: String,
        departure: String,
        startDate: Object,
        endDate: Object,
        // rate plan
        ratePlan: String,
        // misc.
        fields: Array,
    },
    computed: {
        isTooLong () {
            return this.stayLength > this.stayLengthMax
        },
        // components
        datepicker () {
            let date = this.startDate
            if (this.departure && this.selecting === 'departure') {
                date = new Date(this.departure)
            } else if (this.arrival) {
                date = new Date(this.arrival)
            }
            return {
                isSelectable: true,
                // availability
                availability: this.availability,
                hasAvailabilityData: this.hasAvailabilityData,
                isGettingAvailability: this.isGettingAvailability,
                stayRules: this.stayRules,
                selecting: this.selecting,
                // dates
                date,
                arrival: this.arrival,
                departure: this.departure,
                startDate: this.startDate,
                endDate: this.endDate,
                // rate plan
                ratePlan: this.ratePlan,
                // slides
                slideCount: this.slideCount,
                slideSize: this.slideSize,
            }
        },
        // slides
        slideCount () {
            return store.breakpoint.includes('s') ? 1 : 2
        },
        slideSize () {
            return store.breakpoint.includes('xl') ? 'lg' : 'base'
        },
    },
    methods: {
        close () {
            this.hideDatepicker()
            this.revertDates()
        },
        documentClick ($event) {
            if (this.$el.contains($event.target)) return
            if (
                this.isDatepickerExpanded &&
                this.revertExclusionSelectors.findIndex(e => $event.target.matches(e)) < 0
            ) {
                this.close()
            } else {
                this.hideDatepicker()
            }
        },
        toggleDate ($event) {
            const { selecting: key } = this
            const { event } = this.fields.find(e => e.key === key)
            const { date } = $event.detail
            const value = date.toISOString().slice(0,10)
            // tapping the check-out a weekly resort filled in confirms it rather than clearing it
            const isConfirming = key === 'departure' && value === getDefaultDeparture(this.stayRules, this.arrival)
            this.$emit(event, value === this[key] && !isConfirming ? null : $event)
        },
        revertDates () {
            this.fieldsOnOpen.forEach(e => this.$emit(e.event, { detail: { date: e.value && new Date(e.value) } }))
        },
        updateSearch () {
            this.hideDatepicker()
            this.$emit('update-search')
        },
        // datepicker
        hideDatepicker () {
            this.isDatepickerExpanded = false
            this.selecting = null
            // closed, the page describes the chosen dates again rather than the months last browsed
            this.$emit('visible-months', { detail: { months: null } })
        },
        showDatepicker (selecting = null) {
            this.isDatepickerExpanded = true
            this.selecting = selecting || 'arrival'
        },
        toggleDatepicker (selecting = null) {
            if (this.isDatepickerExpanded && selecting === this.selecting) {
                this.hideDatepicker()
            } else {
                this.showDatepicker(selecting)
            }
        },
    },
    watch: {
        isDatepickerExpanded (newVal,oldVal) {
            if (newVal && !oldVal) {
                this.arrivalOnOpen = this.arrival
                this.departureOnOpen = this.departure
                this.fieldsOnOpen = this.fields
            }
        },
        arrival () {
            // with stay rules the check-out may have been filled in with the arrival, and the next click extends it
            if (this.isDatepickerExpanded && this.arrival && (!this.departure || this.stayRules)) {
                this.selecting = 'departure'
            }
        },
        departure () {
            if (this.isDatepickerExpanded && this.departure && !this.arrival) {
                this.selecting = 'arrival'
            }
        },
    },
    // lifecycle
    mounted () {
        document.addEventListener('click', this.documentClick)
    },
    unmounted () {
        document.removeEventListener('click', this.documentClick)
    },
    // misc.
    components: {
        Btn: BtnComoponent,
        DateField: DateFieldComponent,
        Datepicker: DatepickerComponent,
        DisabledTip: DisabledTipComponent,
    },
}
</script>

<template>
    <div class="tw:relative tw:box-border tw:**:box-border">
        <!-- inputs -->
        <div
            class="
                tw:grid
                tw:grid-cols-2
                tw:gap-3
                tw:h-full
                tw:lg:gap-0
            "
        >
            <div
                class="
                    tw:relative
                    tw:border
                    tw:border-solid
                    tw:border-ccc
                    tw:lg:last:border-l-0
                "
                v-for="field in fields"
            >
                <DateField
                    v-on:change="$emit(field.event, $event.target.value)"
                    v-bind="field"
                />
                <button
                    aria-haspopup="true"
                    class="
                        tw:block
                        tw:absolute
                        tw:z-10
                        tw:inset-0
                        tw:top-0
                        tw:indent-[-9999em]
                        tw:bg-[rgba(255,0,0,0)]
                        tw:border-none
                    "
                    type="button"
                    v-text="`toggle datepicker: select ${field.key} date`"
                    v-on:click="toggleDatepicker(field.key)"
                    v-bind:aria-expanded="isDatepickerExpanded"
                ></button>
            </div>
        </div>
        <!-- datepicker -->
        <div
            class="
                tw:relative
                tw:w-full
                tw:mt-5
                tw:lg:absolute
                tw:lg:top-full
                tw:lg:z-10
            "
            role="status"
            v-if="isDatepickerExpanded"
        >
            <div
                class="
                    tw:absolute
                    tw:top-px
                    tw:z-10
                    tw:w-4
                    tw:h-4
                    tw:-m-2
                    tw:indent-[-9999em]
                    tw:bg-white
                    tw:border-0
                    tw:border-t
                    tw:border-l
                    tw:border-solid
                    tw:border-ccc
                    tw:transition-[left]
                    tw:rotate-45
                "
                v-text="`selecting ${ selecting } date`"
                v-bind:class="{
                    'tw:left-1/2': !selecting,
                    'tw:left-1/4': selecting === 'arrival',
                    'tw:left-3/4': selecting === 'departure',
                }"
            ></div>
            <div
                class="
                    tw:p-5
                    tw:w-full
                    tw:bg-white
                    tw:border
                    tw:border-solid
                    tw:border-ccc
                    tw:xs:p-10
                    tw:sm:px-0
                    tw:lg:absolute
                    tw:lg:top-full
                    tw:lg:px-14
                    tw:lg:py-12
                    tw:lg:w-fit
                    tw:lg:min-w-full
                "
                style="grid-column: 1 / -1;"
                v-on:click.stop
            >
                <Datepicker
                    class="tw:relative tw:z-0"
                    v-on:click.prevent
                    v-on:toggle-date="toggleDate"
                    v-on:visible-months="$emit('visible-months', $event)"
                    v-bind="datepicker"
                >
                    <template v-slot:header v-if="$slots.datepickerHeader">
                        <slot name="datepickerHeader"></slot>
                    </template>
                    <template v-slot:actions>
                        <Btn
                            v-if="hasClearDatesBtn && (arrival || departure)"
                            v-on:click="$emit('update-arrival', null)"
                            v-bind:class="{ 'tw:w-full': slideCount < 2 }"
                        >Clear Dates</Btn>
                        <Btn
                            v-on:click="close"
                            v-bind="{ isSecondary: true }"
                            v-bind:class="{ 'tw:w-full': slideCount < 2 }"
                        >Close</Btn>
                        <DisabledTip
                            v-bind:class="{ 'tw:w-full': slideCount < 2 }"
                            v-bind="{
                                isActive: isTooLong,
                                tooltip: { text: `Cannot book more than ${stayLengthMax} nights`, align: 'end', maxWidth: 320 },
                            }"
                        >
                            <Btn
                                v-on:click="updateSearch"
                                v-bind="{
                                    isPrimary: true,
                                    isDisabled: isTooLong || (arrival === arrivalOnOpen && departure === departureOnOpen),
                                    type: 'submit',
                                }"
                            >Update Search</Btn>
                        </DisabledTip>
                    </template>
                </Datepicker>
            </div>
        </div>
    </div>
</template>
